import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styled from '@emotion/styled';
import { getCoordinates } from './countryCoordinates';

// Dark theme marker icon - small glowing circle
const darkThemeIcon = L.divIcon({
  className: 'dark-theme-marker',
  iconSize: [8, 8],
  iconAnchor: [4, 4], // Center the icon on the coordinates
  html: '<span class="marker-dot"></span>'
});

const MapWrapper = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    z-index: 1;
    background: #09090b;

    .leaflet-container {
        width: 100%;
        height: 100%;
        background: #09090b;
    }

    /* Dark theme circle markers with glow */
    .dark-theme-marker {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .marker-dot {
        width: 8px;
        height: 8px;
        background: #f97316;
        border-radius: 50%;
        display: block;
        box-shadow: 0 0 8px rgba(249, 115, 22, 0.8), 0 0 16px rgba(249, 115, 22, 0.4);
    }

    /* Dark theme popup styling */
    .leaflet-popup-content-wrapper {
        background: rgba(24, 24, 27, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    }

    .leaflet-popup-tip {
        background: rgba(24, 24, 27, 0.95);
        border: none;
        box-shadow: none;
    }

    .leaflet-popup-close-button {
        color: #a1a1aa !important;
        &:hover {
            color: #fff !important;
        }
    }

    /* Hide attribution */
    .leaflet-control-attribution {
        display: none;
    }
`;

interface LeafletMapProps {
  activeCountries: string[];
}

const LeafletMap: React.FC<LeafletMapProps> = ({ activeCountries = [] }) => {
  const [mounted, setMounted] = useState(false);
  const [geoJsonData, setGeoJsonData] = useState<any>(null);

  useEffect(() => {
    setMounted(true);
    fetch('https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json')
      .then(res => res.json())
      .then(data => setGeoJsonData(data))
      .catch(err => console.error('Failed to load map data', err));
  }, []);

  if (!mounted) return null;

  return (
    <MapWrapper>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        scrollWheelZoom={false}
        attributionControl={false}
        zoomControl={false}
        dragging={true}
        doubleClickZoom={false}
        style={{ height: '100%', width: '100%', background: '#09090b' }}
      >
        {/* Dark theme tile layer as fallback */}
        {!geoJsonData && (
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
            opacity={0.5}
          />
        )}

        {/* Country outlines with dark theme styling */}
        {geoJsonData && (
          <GeoJSON
            data={geoJsonData}
            style={{
              fillColor: 'rgba(255, 255, 255, 0.03)',
              weight: 1,
              opacity: 0.6,
              color: 'rgba(255, 255, 255, 0.25)',
              fillOpacity: 1
            }}
          />
        )}

        {/* Active country markers */}
        {activeCountries.map((country, index) => {
          const coords = getCoordinates(country);
          if (!coords) return null;

          return (
            <Marker
              key={`${country}-${index}`}
              position={coords}
              icon={darkThemeIcon}
            >
              <Popup>
                <div style={{
                  fontWeight: 600,
                  color: '#fff',
                  marginBottom: '4px',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.85rem'
                }}>
                  {country}
                </div>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#a1a1aa',
                  fontFamily: 'Inter, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Active Deployment
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </MapWrapper>
  );
};

export default LeafletMap;
